"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { useTranslations } from "next-intl";
import {
  ArrowUpIcon,
  MapTrifoldIcon,
  ShareNetworkIcon,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";

/** 말풍선은 방문(탭)당 한 번 — sessionStorage 라 탭을 닫으면 다음 방문에 다시 보여준다. */
const TIP_SEEN_KEY = "loc:product-map-tip-seen";
/** 이만큼 내리면 말풍선을 닫는다(상단 첫 화면에서만 보이게). */
const TIP_HIDE_SCROLL_PX = 120;
const TIP_DELAY_MS = 600;
const TIP_MAX_MS = 5000;

interface ProductFloatingActionsProps {
  /** 지점이 많은 상품의 큰 지도(검색 페이지) 링크 — 없으면 지도 버튼을 숨긴다. */
  bigMapHref: string | null;
  onShare: () => void;
  /**
   * mobile: 하단 바 위, 40px, 말풍선은 상단에서만 잠깐.
   * desktop: 화면 오른쪽 아래 고정(right-6 bottom-6), 48px, 말풍선은 상단에서만(버튼 왼쪽).
   */
  variant?: "mobile" | "desktop";
}

function readTipSeen(): boolean {
  try {
    return window.sessionStorage.getItem(TIP_SEEN_KEY) === "1";
  } catch {
    return false;
  }
}

function markTipSeen() {
  try {
    window.sessionStorage.setItem(TIP_SEEN_KEY, "1");
  } catch {
    // 저장 불가(사파리 개인정보 모드 등) — 이번 방문에서 한 번 더 보일 뿐이다.
  }
}

/**
 * 상품 상세 모바일 우측 하단 원형 버튼 묶음: 지도 · 공유 · 맨 위로.
 *
 * MobileBookingDrawer 의 하단 바 바로 위(bottom-full)에 붙어서, 보증금 안내로 바 높이가
 * 달라져도 가려지지 않는다. 지도 버튼은 커버리지 모드(지점 20곳 이상)일 때만, 맨 위로는 한 화면 이상
 * 내렸을 때만 나온다.
 */
export default function ProductFloatingActions({
  bigMapHref,
  onShare,
  variant = "mobile",
}: ProductFloatingActionsProps) {
  const isDesktop = variant === "desktop";
  const t = useTranslations("product.floating");
  const [showTop, setShowTop] = useState(false);
  const [showTip, setShowTip] = useState(false);
  // 데스크톱 말풍선: 상단(TIP_HIDE_SCROLL_PX 이내)에 있는 동안만.
  const [atTop, setAtTop] = useState(true);
  // 데스크톱: 푸터 높이만큼 위에 고정(bottom = 푸터 높이 + 24px). 화면 폭에 따라 푸터 높이가
  // 달라지므로 마운트·리사이즈 때 잰다.
  const [footerHeight, setFooterHeight] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      setShowTop(window.scrollY > window.innerHeight);
      setAtTop(window.scrollY <= TIP_HIDE_SCROLL_PX);
      if (!isDesktop && window.scrollY > TIP_HIDE_SCROLL_PX) setShowTip(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isDesktop]);

  useEffect(() => {
    if (!isDesktop) return;
    const footer = document.querySelector("footer");
    if (!footer) return;
    const measure = () => setFooterHeight(footer.offsetHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(footer);
    return () => ro.disconnect();
  }, [isDesktop]);

  useEffect(() => {
    if (isDesktop || !bigMapHref || readTipSeen()) return;
    const show = window.setTimeout(() => {
      // 이미 내려 둔 상태(뒤로 가기 복원 등)면 띄우지 않는다.
      if (window.scrollY > TIP_HIDE_SCROLL_PX) return;
      setShowTip(true);
      markTipSeen();
    }, TIP_DELAY_MS);
    const hide = window.setTimeout(() => setShowTip(false), TIP_DELAY_MS + TIP_MAX_MS);
    return () => {
      window.clearTimeout(show);
      window.clearTimeout(hide);
    };
  }, [bigMapHref, isDesktop]);

  const circle = cn(
    "flex cursor-pointer items-center justify-center rounded-full border shadow-[0_2px_10px_rgba(0,23,51,0.1)] transition-transform active:scale-95",
    isDesktop ? "h-12 w-12 hover:shadow-[0_4px_14px_rgba(0,23,51,0.14)]" : "h-10 w-10",
  );
  const icon = isDesktop ? "h-5.5 w-5.5" : "h-4.5 w-4.5";
  const neutral = cn(circle, "border-gray-200 bg-white text-gray-600", isDesktop && "hover:bg-gray-50");

  const mapButton = bigMapHref && (
    <div className="relative flex items-center">
      {isDesktop ? (
        // 데스크톱: 상단에 있을 때만 버튼 왼쪽에 표시, 조금 내리면 사라진다(맨 위로 오면 다시).
        <span
          aria-hidden={!atTop}
          className={cn(
            "pointer-events-none absolute right-full mr-2.5 rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-white shadow-lg transition-all duration-300 motion-reduce:transition-none",
            "after:absolute after:top-1/2 after:-right-1.25 after:-mt-1.25 after:border-y-5 after:border-l-5 after:border-y-transparent after:border-l-gray-900",
            atTop ? "translate-x-0 opacity-100" : "translate-x-1.5 opacity-0",
          )}
        >
          {t("mapTip")}
        </span>
      ) : (
        <span
          role="status"
          aria-hidden={!showTip}
          className={cn(
            "pointer-events-none absolute right-12.5 rounded-lg bg-gray-900 px-2.5 py-1.5 text-xs font-medium whitespace-nowrap text-white shadow-lg transition-all duration-300 motion-reduce:transition-none",
            "after:absolute after:top-1/2 after:-right-1.25 after:-mt-1.25 after:border-y-5 after:border-l-5 after:border-y-transparent after:border-l-gray-900",
            showTip ? "translate-x-0 opacity-100" : "translate-x-1.5 opacity-0",
          )}
        >
          {t("mapTip")}
        </span>
      )}
      <Link
        href={bigMapHref}
        aria-label={t("mapTip")}
        className={cn(
          circle,
          "border-orange-300 bg-orange-50 text-orange-600",
          isDesktop && "hover:bg-orange-100",
        )}
      >
        <MapTrifoldIcon className={icon} />
      </Link>
    </div>
  );

  const shareButton = (
    <button type="button" onClick={onShare} aria-label={t("share")} className={neutral}>
      <ShareNetworkIcon className={icon} />
    </button>
  );

  // 항상 렌더하고 높이·투명도·크기로 접었다 편다 — 나타날 때 위 버튼들이 툭 밀리지 않게.
  // 접힌 동안 -mt-2 로 gap(8px)까지 없애고, 포커스·클릭도 막는다.
  const topButton = (
    <div
      aria-hidden={!showTop}
      className={cn(
        "transition-[max-height,opacity,margin] duration-300 ease-out motion-reduce:transition-none",
        showTop ? "max-h-12 opacity-100" : "pointer-events-none -mt-2 max-h-0 opacity-0",
      )}
    >
      <button
        type="button"
        tabIndex={showTop ? undefined : -1}
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        aria-label={t("scrollTop")}
        className={cn(
          neutral,
          "duration-300 ease-out",
          showTop ? "scale-100" : "scale-75",
        )}
      >
        <ArrowUpIcon className={icon} />
      </button>
    </div>
  );

  // 순서는 모바일·데스크톱 동일: 지도 · 공유 · 맨 위로
  return (
    <div
      style={isDesktop ? { bottom: footerHeight + 24 } : undefined}
      className={cn(
        "flex flex-col items-end gap-2",
        isDesktop && "fixed right-6 bottom-6 z-40",
      )}
    >
      {mapButton}
      {shareButton}
      {topButton}
    </div>
  );
}
